/**
 * The gear in the arena, and the modal behind it: what the overlay draws — and, since Battery
 * saver, what it costs to draw it — per player.
 *
 * One button and one dialog rather than a control per panel, because every one of these is a
 * decision a player makes once — before or between runs — and a HUD that grew a little cross on
 * each corner would cost more arena than the panels do.
 *
 * Three rules, and the third is the one that matters:
 *
 * - **The gear is always drawn.** It is in the top-right column, above the leaderboard, on every
 *   surface and in both layouts — including the portrait one, where Leave has gone down to the
 *   footer and this corner is otherwise empty. It is never one of the things a setting can hide.
 * - **Nothing here is a control that hides itself.** Turning the action buttons off leaves the
 *   keyboard exactly as it was and leaves Leave exactly where it was, and the switch that put
 *   them away is in a dialog that has nothing to do with them. There is no state this modal can
 *   produce that only the hidden control could undo.
 * - **The dialog is `components/ui/dialog`'s**, so Escape and a tap on the backdrop close it and
 *   the focus trap, the portal and the close button are the app's own rather than a second
 *   implementation of them. **It stays a modal**, unlike the room roster and the sayings picker,
 *   which were moved off `Dialog` onto `ArenaPanel` so the arena keeps steering behind them. This
 *   one is a deliberate pause: a dozen switches read one at a time by somebody who is not
 *   steering, and — the half that decides it — a screen full of controls a stray SPACE would
 *   flip. `keyboardActs` refusing the action keys under a `role="dialog"` is exactly right here,
 *   and that predicate was left alone; what changed is which overlays claim to be one.
 *
 * The switches write straight through to `hudSettings`; there is no draft and no Save. A toggle
 * whose effect is a panel appearing behind the modal is its own confirmation, and a player who
 * turned the wrong one off can see that and put it back in the same gesture.
 */

import { useState, useSyncExternalStore } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { hudSettings, type HudSettingKey, type HudSettings } from '../state/hud-settings';
import { useTapActivate } from './tap-activate';

/** The overlay preferences, as a React value. See `hudSettings` for why it is a singleton. */
export function useHudSettings(): HudSettings {
  return useSyncExternalStore(hudSettings.subscribe, hudSettings.getSnapshot);
}

interface SettingRow {
  key: HudSettingKey;
  label: string;
  hint: string;
}

/**
 * The rows, in the order a player reads the screen: the panels in the corners they occupy, then
 * the two name toggles, then the controls, and Battery saver last — it is the only one that is
 * not about the overlay at all, and the only one that is off to begin with.
 *
 * `sayings` hides the overlay alone — see `HudSettings.sayings` for why the Say button is
 * deliberately not one of the things it takes away — and `notices` is the newest of them, the one
 * switch in this dialog that was asked for in the same breath as the feature behind it.
 */
const PANELS: readonly SettingRow[] = [
  { key: 'leaderboard', label: 'Leaderboard', hint: 'The top of the room, top right' },
  { key: 'stats', label: 'Your run', hint: 'Mass, rank, dots and time alive' },
  { key: 'minimap', label: 'Minimap', hint: 'The whole arena, bottom left' },
  { key: 'population', label: 'Human / bot count', hint: 'How many of the circles are people' },
  { key: 'sayings', label: 'Sayings', hint: 'What people in the room are saying' },
  {
    key: 'notices',
    label: 'Event messages',
    hint: 'Who collected what, and who ate whom — upper centre',
  },
];

const NAMES: readonly SettingRow[] = [
  { key: 'humanNames', label: 'Player names', hint: 'Names over other people’s blobs' },
  { key: 'botNames', label: 'Bot names', hint: 'The cyan BOT labels' },
];

/**
 * The one group that is not about what the overlay draws, and the one switch in this dialog that
 * is **off by default**.
 *
 * The hint says what it costs rather than what it saves, deliberately: how much battery a lower
 * frame rate buys depends on the phone, the panel and the room, and a percentage this dialog
 * cannot stand behind is worse than no number at all. What it *can* promise is the other half —
 * nothing is hidden. See `state/hud-settings.ts`.
 */
const PERFORMANCE: readonly SettingRow[] = [
  {
    key: 'batterySaver',
    label: 'Battery saver',
    hint: 'Softer edges and simpler effects, for longer play on a phone. Nothing is hidden',
  },
  {
    key: 'perfStats',
    label: 'Frame rate readout',
    hint: 'A small line showing the frames a second this device is actually managing',
  },
];

const CONTROLS: readonly SettingRow[] = [
  {
    key: 'actionButtons',
    label: 'On-screen buttons',
    hint: 'Split, eject and fire. The keyboard keeps working either way',
  },
  {
    key: 'joystick',
    label: 'Thumb stick',
    hint: 'A pad on the right to steer with. Dragging the arena keeps working either way',
  },
  {
    // **THIS ROW IS THE WAY BACK, WHICH IS WHY THE HINT NAMES IT.** The ✕ on the hint itself
    // writes this same setting (see `ControlHint`), so a player who dismissed it has exactly one
    // place to look — and this line has to say that the switch is what turns it back on, because
    // by the time somebody reads it the panel it describes is not on screen.
    key: 'controlHint',
    label: 'Control hints',
    hint: 'The key bindings along the bottom. Dismissing them is this switch',
  },
];

/**
 * The gear and its dialog, as one thing the HUD drops into a corner.
 *
 * Open state is local: nothing else in the app has an opinion about whether this modal is up,
 * and putting it in the store would make it a preference that persisted into the next run.
 */
export function HudSettingsControl() {
  const [open, setOpen] = useState(false);
  const openSettings = useTapActivate(() => setOpen(true));

  return (
    <>
      <button
        type="button"
        data-testid="hud-settings-button"
        aria-label="Overlay settings"
        title="Overlay settings"
        // `pointerup` rather than `click`: the gear is drawn over a live arena, and a thumb on
        // the joystick means the platform reports no tap. See `./tap-activate.ts`.
        {...openSettings}
        className="pointer-events-auto grid size-11 shrink-0 place-items-center rounded-lg border border-border/40 bg-card/40 text-muted-foreground shadow-lg backdrop-blur-sm transition-colors hover:text-foreground sm:size-10"
      >
        <GearGlyph />
      </button>
      <HudSettingsDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function HudSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Overlay"
      description="What the arena draws over the map, and what it costs to draw. Kept on this device."
      // A dozen rows do not fit a phone held sideways, which is the shape that has the least
      // height and the most of this game on screen. Scrolling the card is what keeps the last
      // switch reachable there; Escape and the backdrop close it from anywhere in the scroll.
      className="max-h-[85dvh] overflow-y-auto"
    >
      <div data-testid="hud-settings-dialog" className="flex flex-col gap-4">
        <Group heading="Panels" rows={PANELS} />
        <Group heading="Names" rows={NAMES} />
        <Group heading="Controls" rows={CONTROLS} />
        <Group heading="Performance" rows={PERFORMANCE} />
      </div>
    </Dialog>
  );
}

function Group({ heading, rows }: { heading: string; rows: readonly SettingRow[] }) {
  return (
    <section className="flex flex-col gap-0.5">
      <h3 className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {heading}
      </h3>
      {rows.map((row) => (
        <Toggle key={row.key} row={row} />
      ))}
    </section>
  );
}

/**
 * One switch. A real checkbox with `role="switch"`, visually hidden behind the track it drives —
 * so it is a thumb-sized target, and it is still the control a keyboard and a screen reader get.
 */
function Toggle({ row }: { row: SettingRow }) {
  const settings = useHudSettings();
  const checked = settings[row.key];

  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg px-1 py-2 transition-colors hover:bg-secondary/40">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{row.label}</span>
        <span className="block text-xs text-muted-foreground">{row.hint}</span>
      </span>
      <input
        type="checkbox"
        role="switch"
        // Redundant beside a checkbox's own `checked`, and required by the role: an assistive
        // technology that reads this as a switch reads its state from here.
        aria-checked={checked}
        className="peer sr-only"
        checked={checked}
        onChange={() => hudSettings.toggle(row.key)}
      />
      <span
        aria-hidden="true"
        className="relative h-6 w-11 shrink-0 rounded-full border border-border/60 bg-secondary transition-colors after:absolute after:content-[''] after:left-1 after:top-1 after:size-4 after:rounded-full after:bg-muted-foreground after:transition-transform peer-checked:bg-primary/30 peer-checked:after:translate-x-5 peer-checked:after:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
      />
    </label>
  );
}

/** A gear, inline, for the reason every other glyph in the HUD is inline: no icon dependency. */
function GearGlyph() {
  return (
    <svg
      className="size-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
